import React, { useState, useEffect, useRef } from 'react';
import style from './AiPrediction.module.css';
import GaugeChart from '../../components/charts/GaugeChart';
import SparkleIcon from '../../components/home/SparkleIcon';
import axiosApi from '../../api/daonAxios';
import noDataImg from '../../images/common/i_3d_no_data_doc.svg';

const DISEASE_TABS = [
    { key: 'ckd', label: 'CKD' },
    // { key: 'lung', label: '폐암' },   // 임시 숨김처리 (2026.09.18)
];

const MOCK_DATA = {
    ckd: {
        title: '만성신장질환 예측 결과',
        riskPercent: 40,
        gaugeAngle: 30.33333,  // 0~59 양호,  60~119 주의,  120~180 위험  
        diseaseName: 'CKD',
        organName: '신장',
        advice: '디팜님, 생활 습관 관리가 필요합니다.\n<b>eGFR</b>은 수분 섭취와 저염식으로 관리하고,\n<b>단백뇨</b>는 단백질 섭취를 조절해보세요.\n<b>BMI</b>는 운동과 식단으로 개선해보세요.',
    },
};


const formatProbability = (value) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const num = Number(value);

    if (Number.isNaN(num)) {
        return '-';
    }

    return (num * 100).toFixed(2);
};

const CKD_LEVEL_COLOR = {
    'GRD-G': '#3284FF',
    'GRD-C': '#FF8D29',
    'GRD-I': '#E55151',
};



const getCheckupLevel = (value, key) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    // 요단백 정성검사
    if (key === 'protein') {
        const protein = String(value).trim().toUpperCase().replace(/\s/g, '');

        let level;

        if (['NEGATIVE', 'NEG', 'TRACE'].includes(protein)) {
            level = 'good';
        } else if (['1+', '+1', '2+', '+2'].includes(protein)) {
            level = 'warn';
        } else if (['3+', '+3', '4+', '+4'].includes(protein)) {
            level = 'danger';
        } else {
            return null;
        }

        return {
            t: {
                good: '양호',
                warn: '주의',
                danger: '중점관리'
            }[level],
            level
        };
    }

    const num = Number(value);

    if (!Number.isFinite(num)) {
        return null;
    }

    let level;

    switch (key) {
        case 'egfr':
            level = num >= 90 ? 'good' : num >= 60 ? 'warn' : 'danger';
            break;
        case 'brssrTop':
            level = num < 130 ? 'good' : num < 140 ? 'warn' : 'danger';
            break;
        case 'brssrLwet':
            level = num < 80 ? 'good' : num < 90 ? 'warn' : 'danger';
            break;
        case 'bmi':
            level = num < 23 ? 'good' : num < 25 ? 'warn' : 'danger';
            break;
        case 'hba1c':
            level = num < 5.7 ? 'good' : num < 6.5 ? 'warn' : 'danger';
            break;
        default:
            return null;
    }

    return {
        t: {
            good: '양호',
            warn: '주의',
            danger: '중점관리'
        }[level],
        level
    };
};



/* ── 메인 컴포넌트 ── */
const AiPrediction = () => {
    const [diseaseTab, setDiseaseTab] = useState('ckd');
    const [animate, setAnimate] = useState(false);
    const wrapRef = useRef(null);

    const [ckdResult, setCkdResult] = useState({});

    const [latestCheckup, setLatestCheckup] = useState(null);

    
    const indicators = [
        {
            key: 'egfr',
            label: 'eGFR',
            value: latestCheckup?.egfr ?? '-',
            ...(() => {
                const result = getCheckupLevel(latestCheckup?.egfr, 'egfr');
                return {
                    level: result?.level ?? null,
                    levelText: result?.t ?? null
                };
            })()
        },
        {
            key: 'protein',
            label: '요단백',
            value: latestCheckup?.uprtn ?? '-',
            ...(() => {
                const result = getCheckupLevel(latestCheckup?.uprtn, 'protein');

                return {
                    level: result?.level ?? null,
                    levelText: result?.t ?? null
                };
            })()
        },
        {
            key: 'bmi',
            label: 'BMI',
            value: latestCheckup?.bmi ?? '-',
            ...(() => {
                const result = getCheckupLevel(latestCheckup?.bmi, 'bmi');
                return {
                    level: result?.level ?? null,
                    levelText: result?.t ?? null
                };
            })()
        },
        {
            key: 'bp',
            label: '혈압',
            value:
                latestCheckup?.brssrTop != null ||
                latestCheckup?.brssrLwet != null
                    ? `${latestCheckup?.brssrTop ?? '-'}/${latestCheckup?.brssrLwet ?? '-'}`
                    : '-',
            ...(() => {
                const top = getCheckupLevel(
                    latestCheckup?.brssrTop,
                    'brssrTop'
                );

                const low = getCheckupLevel(
                    latestCheckup?.brssrLwet,
                    'brssrLwet'
                );

                const result =
                    [top, low].find(v => v?.level === 'danger') ||
                    [top, low].find(v => v?.level === 'warn') ||
                    top || low;

                return {
                    level: result?.level ?? null,
                    levelText: result?.t ?? null
                };
            })()
        }
    ];


    // const ckdLevelName = ckdDgdgrCds.find((i) => i.cdId === ckdResult?.ckdDgdgrCd)?.cdNm ?? '-';

    const ckdLevelColor = CKD_LEVEL_COLOR[ckdResult?.ckdDgdgrCd] ?? '#000000';

    const ckdProbability = Number(ckdResult?.ckdOccrPrbl);

    const ckdGaugeAngle =
        ckdResult?.ckdOccrPrbl === null ||
        ckdResult?.ckdOccrPrbl === undefined ||
        ckdResult?.ckdOccrPrbl === '' ||
        Number.isNaN(ckdProbability)
            ? 0
            : Math.min(Math.max(ckdProbability, 0), 1) * 180;

    
    useEffect(() => {
        setAnimate(false);
        const timer = setTimeout(() => setAnimate(true), 100);
        return () => clearTimeout(timer);
    }, [diseaseTab]);

    // 뷰포트 진입 시 애니메이션
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setAnimate(true); },
            { threshold: 0.3 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const renderHtml = (text) => {
        return text.split('\n').map((line, i) => (
            <span key={i} dangerouslySetInnerHTML={{ __html: line + (i < text.split('\n').length - 1 ? '<br/>' : '') }} />
        ));
    };


    const data = MOCK_DATA[diseaseTab];

    const getCkdResult = async () => {
        try {

            const loginUser = JSON.parse(sessionStorage.getItem('loginUser'));

            const jToken = localStorage.getItem("jToken");

            const patntRgstId = loginUser.usrHsptlMap.find((a) => a.cnncHsptlCd === "012").patntRgstId;

            const requestOptions = {
                params : {
                    patntRgstId: patntRgstId,
                },
                headers: {
                    "Authorization": `Bearer ${jToken}`,
                },
            }
            
            const response = await axiosApi.get('/lab/ckd-result', requestOptions);

            const ckdResult = response.data.result.ckdResult;
            const checkupList = response.data.result.checkupList;

            setLatestCheckup(checkupList.length > 0 ? checkupList[checkupList.length - 1] : null);

            setAnimate(false);
            setCkdResult(ckdResult);

            setTimeout(() => {
                setAnimate(true);
            }, 100);

        } catch (error) {
            console.error('CKD 조회 실패', error);
        } finally {
        }
    };


    useEffect(()=> {
        getCkdResult();
    },[])

    return (
        <div className={style.wrap} ref={wrapRef}>

            {latestCheckup ? 
            <>
                {/* 질환 탭 */}
                <div className={style.diseaseTabs}>
                    {DISEASE_TABS.map(tab => (
                        <button
                            key={tab.key}
                            className={`${style.diseaseBtn} ${diseaseTab === tab.key ? style.diseaseActive : ''}`}
                            onClick={() => setDiseaseTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                {/* 제목 */}
                <h3 className={style.title}>{data.title}</h3>

                {/* 게이지 카드 */}
                <div className={style.gaugeCard}>
                    <GaugeChart angle={ckdGaugeAngle} animate={animate} />
                    <p className={style.gaugeDesc}>
                        {/* 현재 {data.organName} 건강은 <em style={{ color: gaugeLevel.color }}>{gaugeLevel.text}</em> 단계입니다.<br />
                        10년 내 {data.diseaseName} 발병 위험은 <em style={{ color: gaugeLevel.color }}>약 {data.riskPercent}%</em>로 예측됩니다. */}

                        현재 신장 건강은 <em style={{ color: ckdLevelColor  }}>{ckdResult?.ckdDgdgrCdNm}</em> 단계입니다.<br />
                        10년 내 CKD 발병 위험은 <em style={{ color: ckdLevelColor  }}>약 {formatProbability(ckdResult?.ckdOccrPrbl)}%</em>로 예측됩니다.
                    </p>
                </div>

                {/* AI 조언 */}
                {/* 아직 정리가 안되서 안보이게 수정 */}
                {/* <div className={style.adviceCard}>
                    <SparkleIcon size={20} />
                    <p className={style.adviceText}>{renderHtml(data.advice)}</p>
                </div> */}

                {/* 위험 예측 지표 */}
                <h3 className={style.indicatorTitle}>위험 예측 지표</h3>
                <div className={style.indicatorGrid}>
                    {indicators.map(ind => (
                        <div key={ind.key} className={style.indicatorCard}>
                            <span className={style.indicatorLabel}>{ind.label}</span>
                            <span className={style.indicatorIcon} data-icon={ind.key}></span>
                            <strong className={`${style.indicatorValue} ${ind.level ? style[`val_${ind.level}`] : ''}`}>
                                {ind.value}
                            </strong>

                            {ind.level && (
                                <span className={`${style.indicatorBadge} ${style[`badge_${ind.level}`]}`}>
                                    {ind.levelText}
                                </span>
                            )}

                        </div>
                    ))}
                </div>

                {/* 면책 조항 */}
                <p className={style.disclaimer}>
                    AI 건강 예측은 건강검진 결과를 바탕으로<br />
                    위험 요인을 미리 확인하고 예방을 돕습니다.<br />
                    ※ 본 결과는 참고용이며, 필요 시 전문가와 상담해 주세요.
                </p>

            </> :
            <>
                <div className={style['no-data-box']}>
                    <div className={style['img-box']}>
                        <img src={noDataImg} aria-hidden="true" />
                    </div>
                    <p>검진 데이터 등록 대기 중이에요</p>
                    <span>
                        의료진이 건강검진 데이터를 등록하면 <br/>
                        AI 분석 결과가 제공됩니다.
                    </span>
                </div>
                    
            </>
            }

        </div>
    );
};

export default AiPrediction;